import { Trans } from '@lingui/macro'
import {
    Cancel,
    CheckCircle,
    CheckCircleOutlineOutlined,
    Circle,
    CircleOutlined,
    HighlightOffOutlined
} from '@mui/icons-material'
import {
    Box,
    Grid,
    Step,
    StepIcon,
    StepIconProps,
    StepLabel,
    Stepper,
    SvgIcon,
    Typography
} from '@mui/material'
import React, { FC, useEffect, useRef } from 'react'
import useMeasure from 'react-use-measure'
import FormStepperDropdown from './components/form-stepper/form-stepper-dropdown'
import {
    getValidCurrentStepIndex,
    sectionConditionId
} from './formHelpersConditions'
import { StepperFeatStickyComponentController } from './StepperFeatStickyComponentController'

const BUTTON_SIZES = {
  NORMAL: {
    DOWNLOAD: 170,
    SAVE: 95,
    EXTRA: 290,
    GAP: 16
  },
  COMPACT: {
    DOWNLOAD: 66,
    SAVE: 66,
    EXTRA: 66,
    GAP: 0
  }
} as const

interface FormStepperProps {
  formType: 'editable' | 'pdf' | 'fillable-pdf' | 'printable'
  disabledIds: string[]
  sections: any[]
  parsedErrors: any[]
  currentStep: number
  handleStepChange: (newIndex: number) => void
  renderStepLabel: (index: number, section: any) => JSX.Element
  disabled: boolean
  ButtonsElement: React.ReactNode
  downloadButton: boolean
  saveButton: boolean
  renderDropdown?: boolean
  setSmallSpace: (smallSpace: { stepper: boolean, buttons: boolean }) => void
  smallSpace: { stepper: boolean, buttons: boolean }
  extraButtons: Array<() => void>
  showStepperValidation?: boolean
}

const FormStepper = ({
  disabledIds,
  downloadButton,
  saveButton,
  extraButtons = [],
  formType,
  currentStep,
  sections,
  handleStepChange,
  disabled,
  parsedErrors,
  renderStepLabel,
  ButtonsElement,
  setSmallSpace,
  smallSpace,
  showStepperValidation = true,
  ...props
}: FormStepperProps): JSX.Element => {
  const [ref, bounds] = useMeasure()

  const currentStepperState = useRef(smallSpace.stepper)
  const currentButtonsState = useRef(smallSpace.buttons)

  const STEPPER_COLLAPSE_THRESHOLD = -100
  const STEPPER_EXPAND_THRESHOLD = 0
  const BUTTONS_COLLAPSE_THRESHOLD = 30
  const BUTTONS_EXPAND_THRESHOLD = 100

  const calculateButtonsWidth = (isCompact: boolean): number => {
    const sizes = isCompact ? BUTTON_SIZES.COMPACT : BUTTON_SIZES.NORMAL
    let width = 0

    if (downloadButton) width += sizes.DOWNLOAD
    if (saveButton) width += sizes.SAVE
    if (downloadButton && saveButton) width += sizes.GAP
    if (extraButtons.length > 0) width += extraButtons.length * sizes.EXTRA

    return width
  }

  let guttersWidth = 0
  if (downloadButton) {
    guttersWidth += 170
  }
  if (saveButton) {
    guttersWidth += 95
    if (downloadButton) {
      guttersWidth += 16
    }
  }
  if (extraButtons.length > 0) {
    guttersWidth += extraButtons.length * 290
  }

  const renderDropdown: boolean = Boolean(
    smallSpace.stepper || props.renderDropdown
  )
  const sectionsToRedner = sections.filter(
    (section, index) =>
      !disabledIds.includes(sectionConditionId + String(index))
  )

  const stepperSpace =
    bounds.width - guttersWidth - sectionsToRedner.length * 140
  const buttonsSpace = bounds.width - guttersWidth - 300

  const buttonsContainerWidth = calculateButtonsWidth(buttonsSpace < 0)

  useEffect(() => {
    // Hysteresis logic for stepper
    let newStepperState
    if (!currentStepperState.current) {
      newStepperState = stepperSpace < STEPPER_COLLAPSE_THRESHOLD
    } else {
      newStepperState = stepperSpace < STEPPER_EXPAND_THRESHOLD
    }

    // Hysteresis logic for buttons
    let newButtonsState
    if (!currentButtonsState.current) {
      newButtonsState = buttonsSpace < BUTTONS_COLLAPSE_THRESHOLD
    } else {
      newButtonsState = buttonsSpace < BUTTONS_EXPAND_THRESHOLD
    }

    if (
      newStepperState !== currentStepperState.current ||
      newButtonsState !== currentButtonsState.current
    ) {
      currentStepperState.current = newStepperState
      currentButtonsState.current = newButtonsState

      setSmallSpace({
        stepper: newStepperState,
        buttons: newButtonsState
      })
    }
  }, [stepperSpace, buttonsSpace, setSmallSpace])

  useEffect(() => {
    currentStepperState.current = smallSpace.stepper
    currentButtonsState.current = smallSpace.buttons
  }, [smallSpace.stepper, smallSpace.buttons])

  return (
    <Grid
      ref={ref}
      container
      direction='row'
      wrap='nowrap'
      sx={{ mb: '7px', mt: '16px' }}
      gap='16px'
    >
      {/* {stepperSpace - guttersWidth > 0 && !renderDropdown &&
        <Grid sx={{ maxWidth: `${guttersWidth}px`, minWidth: `${guttersWidth}px` }} />} */}
      <Grid item xs>
        {renderDropdown && (
          <Box sx={{ gap: '10px', mt: '16px' }}>
            <Typography
              variant='body2'
              sx={{ fontSize: '14px', lineHeight: '19px', fontWeight: 400 }}
            >
              <Trans>FORM_STEPPER_CURRENT_SECTION</Trans>
            </Typography>
            <FormStepperDropdown
              parsedErrors={parsedErrors}
              renderIcons={formType === 'editable' && showStepperValidation}
              currentStep={currentStep}
              handleStepChange={handleStepChange}
              sections={sections
                .map((section, index) => ({ ...section, trueIndex: index }))
                .filter(
                  (section, index) =>
                    !disabledIds.includes(sectionConditionId + String(index))
                )}
              renderStepLabel={renderStepLabel}
            />
          </Box>
        )}
        {!renderDropdown && (
          <div>
            <Stepper
              nonLinear
              activeStep={getValidCurrentStepIndex({
                realIndex: currentStep,
                disabledIds
              })}
              orientation='horizontal'
              alternativeLabel
            >
              {sections.map((section, index) => {
                if (disabledIds.includes(sectionConditionId + String(index))) {
                  return null
                }

                const hasErrors =
                  parsedErrors.filter((obj) => index === obj.sectionIndex)
                    .length > 0
                return (
                  <Step
                    itemType='step'
                    id='step'
                    key={index}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      handleStepChange(index)
                    }}
                    completed={index < currentStep}
                  >
                    <StepLabel
                      error={hasErrors && formType === 'editable'}
                      id='label'
                      StepIconComponent={
                        formType === 'editable' && showStepperValidation
                          ? EditableFormIcon
                          : ReadOnlyFormIcon
                      }
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          lineHeight: '19px',
                          fontWeight: 600
                        }}
                      >
                        {renderStepLabel(index, section)}
                      </Typography>
                    </StepLabel>
                  </Step>
                )
              })}
            </Stepper>
          </div>
        )}
      </Grid>
      <Grid
        sx={{
          maxWidth: `${buttonsContainerWidth}px`,
          minWidth: `${buttonsContainerWidth}px`
        }}
      >
        {ButtonsElement}
      </Grid>
    </Grid>
  )
}

type FormStepperFeatStickyComponentControllerProps = {
  stickyComponentRef?: React.RefObject<HTMLDivElement>
  isStickyCallback?: (isSticky: boolean) => void
  children: React.ReactNode
} & FormStepperProps

/**
 * This component decides how to render base FormStepper depending on whether the stickyComponentRef element is sticky or not.
 * @param {RefObject<HTMLElement>} props.stickyComponentRef - The ref for the sticky component.
 * @param {Function} props.isStickyCallback - Callback function to notify when the sticky state changes.
 * @param {ReactElement} props - base FormStepper props
 * @returns {ReactElement} The rendered component.
 */
const FormStepperFeatStickyComponentController: FC<
FormStepperFeatStickyComponentControllerProps
> = ({ stickyComponentRef, isStickyCallback, children, ...props }) => {
  return (
    <StepperFeatStickyComponentController
      stickyComponentRef={stickyComponentRef}
      isStickyCallback={isStickyCallback}
    >
      <FormStepper {...props} />
    </StepperFeatStickyComponentController>
  )
}

export const ReadOnlyFormIcon = ({ ...props }: StepIconProps): JSX.Element => {
  const { active = false } = props

  if (active) {
    return (
      <StepIcon
        icon={
          <SvgIcon
            component={Circle}
            color='primary'
            sx={{ width: '24px', height: '24px' }}
          />
        }
      />
    )
  }

  return (
    <StepIcon
      icon={
        <SvgIcon
          component={CircleOutlined}
          color='primary'
          sx={{ width: '24px', height: '24px' }}
        />
      }
    />
  )
}

export const EditableFormIcon = ({ ...props }: StepIconProps): JSX.Element => {
  const { completed = false, active = false, error = false } = props

  if (active) {
    if (error) {
      return (
        <StepIcon
          icon={
            <SvgIcon
              component={Cancel}
              sx={{ width: '24px', height: '24px', color: 'error.main' }}
            />
          }
        />
      )
    } else {
      return (
        <StepIcon
          icon={
            <SvgIcon
              component={CheckCircle}
              sx={{ width: '24px', height: '24px', color: 'success.main' }}
            />
          }
        />
      )
    }
  }

  if (!error && completed) {
    return (
      <StepIcon
        icon={
          <SvgIcon
            component={CheckCircleOutlineOutlined}
            sx={{ width: '24px', height: '24px', color: 'success.main' }}
          />
        }
      />
    )
  }

  if (error) {
    return (
      <StepIcon
        icon={
          <SvgIcon
            component={HighlightOffOutlined}
            sx={{ width: '24px', height: '24px', color: 'error.main' }}
          />
        }
      />
    )
  }

  return (
    <StepIcon
      icon={
        <SvgIcon
          component={CircleOutlined}
          color='primary'
          sx={{ width: '24px', height: '24px' }}
        />
      }
    />
  )
}

export { FormStepperFeatStickyComponentController as FormStepper }

